import React, { useCallback } from 'react';

import { PreviewPanelsProvider } from '@atlassian/preview-panels-api';
import { PARENT_PRODUCTS } from '@atlassian/confluence-page-preview-panel';

import { useClosePreviewPanelOnUrlChange } from './useClosePreviewPanelOnUrlChange';
import { useClosePreviewPanel } from './useClosePreviewPanel';

interface ConfluencePreviewPanelsProviderProps {
	children: React.ReactNode;
	renderers?: any; // Accept renderers as optional prop
}

// Component that uses the URL change detection hooks inside the provider context
const UrlChangeDetector = () => {
	useClosePreviewPanelOnUrlChange();
	return null; // This component doesn't render anything
};

export const ConfluencePreviewPanelsProvider = ({
	children,
}: ConfluencePreviewPanelsProviderProps) => {
	const { closeAllPanels, closeOnlyPreviewPanel } = useClosePreviewPanel();

	const onPreviewPanelOpen = useCallback(() => {
		/*
		Any time a Preview Panel is opened in Confluence, we want to close all
		non-preview panel side panels (like the object sidebar) to ensure no panel conflicts.
		*/
		closeAllPanels();
	}, [closeAllPanels]);

	const onPreviewPanelClose = useCallback(() => {
		// Add any additional logic here for closing preview panels.
		/*
			Any time a Preview Panel is closed in Confluence, if the type of the previous panel and the
			next panel are different, we want to close the previous panel before opening the new one. In order to
			do this we need to pass the preview panel api
		*/
		closeOnlyPreviewPanel();
	}, [closeOnlyPreviewPanel]);

	return (
		<PreviewPanelsProvider
			onPreviewPanelOpen={onPreviewPanelOpen}
			onPreviewPanelClose={onPreviewPanelClose}
			parentProduct={PARENT_PRODUCTS.CONFLUENCE}
		>
			{/* URL change detection hooks need to be inside the provider to access context */}
			<UrlChangeDetector />
			{children}
		</PreviewPanelsProvider>
	);
};
